import { useMemo, useState } from 'react';
import { useBeers } from '../hooks/useBeers';
import { useAssignBeer } from '../hooks/useTapMutations';
import { Icon } from './Icon';
import type { Beer, KegSize, Tap } from '../api/types';

const KEG_SIZES: { value: KegSize; name: string; detail: string }[] = [
  { value: 'half_keg', name: 'Half-barrel', detail: '15.5 gal' },
  { value: 'keg',      name: 'Full barrel',  detail: '31 gal' },
  { value: 'sixtel',   name: 'Sixtel',        detail: '5.2 gal' },
  { value: 'other',    name: 'Other',          detail: '' },
];

export interface SwapModalProps {
  tapNumber: number;
  tapLabel: string | null;
  tapId: string;
  locationId: string;
  fixedKegSize?: KegSize | null;
  onClose: () => void;
  onAssigned: (beerName: string) => void;
}

export function SwapModal({
  tapNumber,
  tapLabel,
  tapId,
  locationId,
  fixedKegSize,
  onClose,
  onAssigned,
}: SwapModalProps) {
  const { data: beersData, isLoading } = useBeers({ status: 'active' });
  const assign = useAssignBeer(locationId);
  const [selectedBeer, setSelectedBeer] = useState<Beer | null>(null);
  const [kegSize, setKegSize] = useState<KegSize>('half_keg');
  const [search, setSearch] = useState('');

  const beers = useMemo(() => beersData?.items ?? [], [beersData]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return beers;
    return beers.filter(
      b =>
        b.name.toLowerCase().includes(q) ||
        b.style.toLowerCase().includes(q),
    );
  }, [beers, search]);

  const effectiveKegSize = fixedKegSize ?? kegSize;

  const handleConfirm = () => {
    if (!selectedBeer) return;
    assign.mutate(
      {
        tapId,
        body: { beerId: selectedBeer.id, status: 'on', kegSize: effectiveKegSize },
      },
      {
        onSuccess: () => onAssigned(selectedBeer.name),
        onError: () => {},
      },
    );
  };

  const displayLabel = tapLabel ? `Tap ${tapNumber} · ${tapLabel}` : `Tap ${tapNumber}`;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
        padding: 24, paddingTop: '5vh', zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-elevated)', color: 'var(--text)',
          borderRadius: 10, width: '100%', maxWidth: 560,
          border: '1px solid var(--border)',
          display: 'flex', flexDirection: 'column', maxHeight: '90vh',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '18px 24px', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ fontSize: '1.125rem', fontWeight: 700 }}>{displayLabel} — choose a beer</div>
          <button onClick={onClose} style={{ color: 'var(--text-muted)', display: 'flex', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
            <Icon name="close" size={20} />
          </button>
        </div>

        {/* Search */}
        <div style={{
          padding: '10px 24px 12px',
          borderBottom: '1px solid var(--border)',
        }}>
          <input
            type="text"
            placeholder="Search beers…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              color: 'var(--text)',
              fontSize: '0.9375rem',
              boxSizing: 'border-box',
              outline: 'none',
            }}
          />
        </div>

        {/* Body */}
        <div style={{ padding: 24, overflow: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
            Select a beer from the catalog to assign to this tap.
          </div>

          {/* Beer list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 228, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {isLoading && (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-subtle)', fontSize: '0.875rem' }}>
                Loading beers…
              </div>
            )}
            {!isLoading && beers.length === 0 && (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-subtle)', fontSize: '0.875rem' }}>
                No active beers in the catalog.
              </div>
            )}
            {!isLoading && beers.length > 0 && filtered.length === 0 && (
              <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-subtle)', fontSize: '0.875rem' }}>
                No beers match &ldquo;{search}&rdquo;.
              </div>
            )}
            {filtered.map(b => (
              <button
                key={b.id}
                onClick={() => setSelectedBeer(b)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '12px 14px', textAlign: 'left',
                  background: selectedBeer?.id === b.id ? 'var(--bg-subtle)' : 'var(--bg)',
                  border: '1px solid',
                  borderColor: selectedBeer?.id === b.id ? 'var(--text)' : 'var(--border)',
                  borderRadius: 6, cursor: 'pointer', transition: 'border-color 0.12s',
                  color: 'var(--text)',
                }}
              >
                <div>
                  <div style={{ fontWeight: 700 }}>{b.name}</div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: 2 }}>
                    {b.style}{b.abv != null ? ` · ${b.abv}% ABV` : ''}
                  </div>
                </div>
                {selectedBeer?.id === b.id && <Icon name="check" size={16} />}
              </button>
            ))}
          </div>

          {/* Keg size cards — hidden when tap has a fixed size */}
          {selectedBeer && !fixedKegSize && (
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontSize: '0.875rem', fontWeight: 700, color: 'var(--text-muted)' }}>
                Keg size
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8 }}>
                {KEG_SIZES.map(({ value, name, detail }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setKegSize(value)}
                    style={{
                      padding: '16px 14px',
                      textAlign: 'left',
                      borderRadius: 8,
                      border: '1px solid',
                      borderColor: kegSize === value ? 'var(--text)' : 'var(--border)',
                      background: kegSize === value ? 'var(--bg-subtle)' : 'var(--bg)',
                      cursor: 'pointer',
                      color: 'var(--text)',
                    }}
                  >
                    <div style={{
                      fontWeight: kegSize === value ? 700 : 500,
                      fontSize: '0.9375rem',
                      color: 'var(--text)',
                    }}>
                      {name}
                    </div>
                    {detail && (
                      <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginTop: 3 }}>
                        {detail}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 24px', borderTop: '1px solid var(--border)',
          display: 'flex', justifyContent: 'flex-end', gap: 8,
        }}>
          <button
            onClick={onClose}
            style={{
              fontWeight: 700, fontSize: '0.9375rem', padding: '10px 16px',
              borderRadius: 6, background: 'var(--bg-subtle)', color: 'var(--text)',
              border: '1px solid var(--border)', cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedBeer || assign.isPending}
            style={{
              fontWeight: 700, fontSize: '0.9375rem', padding: '10px 16px',
              borderRadius: 6, background: 'var(--text)', color: 'var(--bg)',
              border: '1px solid var(--text)', cursor: selectedBeer && !assign.isPending ? 'pointer' : 'not-allowed',
              opacity: !selectedBeer || assign.isPending ? 0.45 : 1,
            }}
          >
            {assign.isPending ? 'Tapping…' : 'Tap keg'}
          </button>
        </div>
      </div>
    </div>
  );
}

export interface SwapModalWrapperProps {
  tapId: string;
  taps: Tap[];
  locationId: string;
  onClose: () => void;
  onAssigned: (name: string) => void;
}

export function SwapModalWrapper({ tapId, taps, locationId, onClose, onAssigned }: SwapModalWrapperProps) {
  const tap = taps.find(t => t.id === tapId);
  if (!tap) return null;
  return (
    <SwapModal
      tapNumber={tap.tapNumber}
      tapLabel={tap.label}
      tapId={tap.id}
      locationId={locationId}
      fixedKegSize={tap.fixedKegSize}
      onClose={onClose}
      onAssigned={onAssigned}
    />
  );
}
